const URL_SRC = "https?://[^\\s<>\"']+";
const TRAILING_PUNCT = /[.,;:!?)]+$/;

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildMentionSrc(names: string[]): string {
  if (names.length === 0) return "@[^\\s]+";
  const sorted = [...names].sort((a, b) => b.length - a.length);
  const alts = sorted.map(escapeRegex).join("|");
  return `@(?:${alts})|@[^\\s]+`;
}

function buildHtmlMentionSrc(names: string[]): string {
  if (names.length === 0) return "@[^\\s<\"'&]+";
  const sorted = [...names].sort((a, b) => b.length - a.length);
  const alts = sorted.map(escapeRegex).join("|");
  return `@(?:${alts})|@[^\\s<"'&]+`;
}

// For plain-text content: linkifies URLs and highlights @mentions.
export function highlightMentions(text: string, names: string[] = []): React.ReactNode {
  const combined = new RegExp(`(${URL_SRC})|(${buildMentionSrc(names)})`, "g");
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;

  while ((m = combined.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      const raw = m[1];
      const url = raw.replace(TRAILING_PUNCT, "");
      const trail = raw.slice(url.length);
      nodes.push(
        <a
          key={m.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="comment-link"
          onClick={(e) => e.stopPropagation()}
        >
          {url}
        </a>,
      );
      if (trail) nodes.push(trail);
    } else {
      nodes.push(<span key={m.index} className="mention">{m[0]}</span>);
    }
    last = combined.lastIndex;
  }

  if (last < text.length) nodes.push(text.slice(last));
  return nodes.length === 0 ? text : <>{nodes}</>;
}

// For sanitized HTML content: linkifies URLs and injects mention spans
// without touching existing HTML tags.
export function highlightMentionsInHtml(sanitizedHtml: string, names: string[] = []): string {
  const combined = new RegExp(
    `(<[^>]+>)|(${URL_SRC})|(${buildHtmlMentionSrc(names)})`,
    "g",
  );
  return sanitizedHtml.replace(
    combined,
    (match, tag: string | undefined, url: string | undefined) => {
      if (tag) return tag;
      if (url !== undefined) {
        const cleanUrl = url.replace(TRAILING_PUNCT, "");
        const trail = url.slice(cleanUrl.length);
        return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="comment-link">${cleanUrl}</a>${trail}`;
      }
      return `<span class="mention">${match}</span>`;
    },
  );
}
